import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type VideoThumbnailImageType = 'image/jpeg' | 'image/png' | 'image/webp';

export interface GenerateThumbnailOptions {
  seekToSeconds?: number;
  maxWidth?: number;
  imageType?: VideoThumbnailImageType;
  quality?: number; // 0..1 (only used for jpeg/webp)
  timeoutMs?: number;
  /**
   * Controls how the browser requests the video bytes.
   * - 'metadata' usually avoids eager full buffering; seeking can still trigger Range requests.
   */
  preload?: 'none' | 'metadata' | 'auto';
  /**
   * For cross-origin protected media that relies on cookies, use 'use-credentials'
   * so the browser can include cookies and still do native Range streaming.
   */
  crossOrigin?: 'anonymous' | 'use-credentials' | null;
}

export interface GeneratedThumbnail {
  blob: Blob;
  objectUrl: string;
  width: number;
  height: number;
  imageType: VideoThumbnailImageType;
}

@Injectable({
  providedIn: 'root'
})
export class VideoThumbnailService {
  constructor(private readonly http: HttpClient) {}

  // Thumbnail generation is CPU/decoder-heavy; limit concurrency so batches (e.g., 9 videos)
  // don't fail randomly due to resource contention.
  private readonly maxConcurrent = 2;
  private activeCount = 0;
  private readonly pendingQueue: Array<() => void> = [];

  runLimited<T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const run = async () => {
        this.activeCount++;
        try {
          resolve(await task());
        } catch (e) {
          reject(e);
        } finally {
          this.activeCount--;
          this.pumpQueue();
        }
      };

      this.pendingQueue.push(run);
      this.pumpQueue();
    });
  }

  private pumpQueue(): void {
    while (this.activeCount < this.maxConcurrent && this.pendingQueue.length > 0) {
      const next = this.pendingQueue.shift();
      if (next) next();
    }
  }

  async generateFromVideoUrl(videoUrl: string, options: GenerateThumbnailOptions = {}): Promise<GeneratedThumbnail> {
    const seekToSeconds = options.seekToSeconds ?? 1;
    // Default to a higher-res thumbnail so it doesn't look blurry when the UI
    // stretches it to fill the card.
    const maxWidth = options.maxWidth ?? 1920;
    const imageType: VideoThumbnailImageType = options.imageType ?? 'image/jpeg';
    const quality = options.quality ?? 0.98;
    const timeoutMs = options.timeoutMs ?? 15000;
    const preload = options.preload ?? 'metadata';
    const crossOrigin = options.crossOrigin ?? 'anonymous';

    const videoEl = document.createElement('video');
    if (crossOrigin) {
      videoEl.crossOrigin = crossOrigin;
    }
    videoEl.preload = preload;
    videoEl.muted = true;
    (videoEl as any).playsInline = true;

    const cleanupListeners: Array<() => void> = [];
    const cleanup = () => {
      cleanupListeners.forEach(fn => fn());
      // clearing src helps some browsers release network/decoder resources
      try {
        videoEl.removeAttribute('src');
        videoEl.load();
      } catch {
        // ignore
      }
    };

    const withTimeout = <T>(promise: Promise<T>): Promise<T> => {
      let timeoutHandle: any;
      const timeoutPromise = new Promise<T>((_, reject) => {
        timeoutHandle = setTimeout(() => reject(new Error('Thumbnail generation timed out')), timeoutMs);
      });
      return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutHandle));
    };

    const once = (target: EventTarget, eventName: string) =>
      new Promise<void>((resolve, reject) => {
        const onEvent = () => {
          target.removeEventListener(eventName, onEvent);
          resolve();
        };
        const onError = () => {
          target.removeEventListener('error', onError);
          reject(new Error('Failed to load video for thumbnail generation'));
        };
        target.addEventListener(eventName, onEvent, { once: true } as any);
        // only attach error listener to the video element
        if (target === videoEl) {
          videoEl.addEventListener('error', onError, { once: true } as any);
          cleanupListeners.push(() => videoEl.removeEventListener('error', onError as any));
        }
        cleanupListeners.push(() => target.removeEventListener(eventName, onEvent as any));
      });

    try {
      videoEl.src = videoUrl;

      await withTimeout(
        (async () => {
          // Ensure metadata is loaded so we know dimensions and duration.
          if (videoEl.readyState < 1) {
            await once(videoEl, 'loadedmetadata');
          }

          const duration = Number.isFinite(videoEl.duration) ? videoEl.duration : 0;
          const safeSeek = duration > 0 ? Math.min(Math.max(seekToSeconds, 0), Math.max(duration - 0.1, 0)) : seekToSeconds;

          // Some browsers require a tiny delay before seeking after setting src.
          try {
            videoEl.currentTime = safeSeek;
          } catch {
            // If seeking fails early, wait for data then try again.
            await once(videoEl, 'loadeddata');
            videoEl.currentTime = safeSeek;
          }

          await once(videoEl, 'seeked');
        })()
      );

      const sourceWidth = videoEl.videoWidth || 0;
      const sourceHeight = videoEl.videoHeight || 0;
      if (!sourceWidth || !sourceHeight) {
        throw new Error('Unable to read video dimensions for thumbnail generation');
      }

      const scale = Math.min(1, maxWidth / sourceWidth);
      const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
      const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas 2D context unavailable');
      }

      // Improve visual quality when scaling frames down.
      // (Has no effect if target size equals source size.)
      ctx.imageSmoothingEnabled = true;
      try {
        // not supported in older browsers, hence try/catch
        (ctx as any).imageSmoothingQuality = 'high';
      } catch {
        // ignore
      }

      // If the video is cross-origin without CORS headers, this can throw.
      ctx.drawImage(videoEl, 0, 0, targetWidth, targetHeight);

      console.log('[VideoThumbnailService] Frame drawn to canvas', {
        videoUrl,
        targetWidth,
        targetHeight,
        imageType
      });

      const blob = await this.canvasToBlob(canvas, imageType, quality);
      const objectUrl = URL.createObjectURL(blob);

      console.log('[VideoThumbnailService] Canvas converted to blob', {
        videoUrl,
        imageType,
        bytes: blob.size
      });

      return {
        blob,
        objectUrl,
        width: targetWidth,
        height: targetHeight,
        imageType
      };
    } finally {
      cleanup();
    }
  }

  async uploadToAzureBlobSasUrl(sasPutUrl: string, blob: Blob, contentType: string): Promise<void> {
    const response = await fetch(sasPutUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': contentType
      },
      body: blob
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Azure Blob upload failed (${response.status}): ${text}`);
    }
  }

  /**
   * Upload thumbnail bytes to your backend (e.g., Node/Express) as multipart/form-data.
   * Your backend can then upload to Azure Blob using credentials (connection string/managed identity).
   */
  async uploadToBackendMultipart(
    apiUrl: string,
    blob: Blob,
    options: { filename: string; fieldName?: string; extraFields?: Record<string, string> }
  ): Promise<any> {
    const fieldName = options.fieldName ?? 'file';
    const form = new FormData();
    form.append(fieldName, blob, options.filename);

    if (options.extraFields) {
      Object.entries(options.extraFields).forEach(([key, value]) => form.append(key, value));
    }

    // Use HttpClient so existing interceptors (MSAL/bearer tokens) apply.
    // Avoid forcing cookies/credentials; CORS + '*' origins would fail with credentials.
    return firstValueFrom(this.http.post(apiUrl, form));
  }

  blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read blob as data URL'));
      reader.onload = () => resolve(String(reader.result || ''));
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Upload thumbnail as JSON (base64 data URL). Useful when multipart parsing is unreliable behind proxies.
   */
  uploadToBackendJson(apiUrl: string, payload: any): Promise<any> {
    return firstValueFrom(this.http.post(apiUrl, payload));
  }

  private canvasToBlob(canvas: HTMLCanvasElement, type: VideoThumbnailImageType, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Canvas toBlob() returned null'));
            return;
          }
          resolve(blob);
        },
        type,
        quality
      );
    });
  }
}